import { useEffectEvent } from '@floating-ui/react/utils';
import { useLocation } from 'react-router';

import type { DescriptorFieldOption } from '@/blocks/active-form/types';
import { useActiveFormStore } from '@/blocks/active-form/useActiveFormStore';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';

import { inferModuleFromPathname } from '../utils/inferModuleFromPathname';

/**
 * Page/form context sent up with an agent turn so the agent knows where the user
 * is and which form is on screen (AID-379; spec
 * `docs/ai-chat-form-prefill-spec.md` §4.2).
 *
 * This shape MUST stay in lockstep with the gateway's `pageContextSchema`
 * (`services/ai-gateway/src/domain/types/page-context.ts`, AID-378). That schema
 * is `.strict()` at every level and the socket handler parses it with
 * `.catch(undefined)` — so an extra or misnamed key does not fail loudly, it
 * silently drops the WHOLE context and the agent quietly loses form awareness.
 * Hence the explicit field-by-field mapping below rather than spreading the
 * store's descriptor straight through.
 */
export interface PageContext {
  /** Current SPA route (`location.pathname`). */
  route: string;
  /** Backend module key inferred from the route, or `null` when none maps. */
  module: string | null;
  /** The form currently on screen; absent when the user isn't on one. */
  activeForm?: {
    entityType: string;
    recordId: string | null;
    fields: {
      key: string;
      label: string;
      displayType: string;
      required: boolean;
      enumOptions?: string[];
      enumValueLabels?: { value: string; label: string }[];
    }[];
  };
}

/**
 * Wire caps on the published option lists, mirroring `pageContextSchema`'s
 * (`MAX_ENUM_OPTIONS` / `MAX_ENUM_OPTION` in the gateway and the agent) and the
 * aggregate `MAX_PAGE_CONTEXT_CHARS`.
 *
 * These matter far more than a cap normally would. The gateway parses the context
 * with `.catch(undefined)`, so a single over-cap array does not fail that FIELD —
 * it silently discards the WHOLE context, and the user loses form awareness with
 * no signal anywhere. Before AID-393 nothing populated `enumOptions`, so the caps
 * were unreachable; a customer-configured custom-attribute select with a couple
 * of hundred options now reaches them.
 *
 * So the option lists are dropped rather than sent whenever they would not fit,
 * per field first and then in aggregate. The cost is asymmetric and worth naming:
 * a field without its value list costs the model a hint it can recover from (the
 * browser still validates every applied value against the full set, so a guess is
 * rejected, not written), whereas an over-cap payload costs the entire feature for
 * that turn.
 */
const MAX_WIRE_ENUM_OPTIONS = 100;
const MAX_WIRE_ENUM_OPTION_CHARS = 256;
const MAX_WIRE_CONTEXT_CHARS = 128_000;

interface WireOptions {
  enumOptions?: string[];
  enumValueLabels?: { value: string; label: string }[];
}

/**
 * A closed-option field publishes its allowed values so the model proposes one of
 * them instead of inventing a plausible label.
 *
 * STRINGIFIED on the wire: a rating value is a number in one taxonomy and a
 * string in another, and `coerceFieldValue` maps the proposal back to the
 * native-typed option — it owns the option set and is the only layer that knows
 * which. The labels ride alongside so the model can reason in the words the user
 * sees on the form.
 */
const toWireOptions = (options: DescriptorFieldOption[]): WireOptions => {
  if (options.length > MAX_WIRE_ENUM_OPTIONS) {
    return {};
  }

  const enumOptions = options.map(({ value }) => String(value));
  const enumValueLabels = options.map(({ value, label }) => ({
    value: String(value),
    label,
  }));
  const fits = enumValueLabels.every(
    ({ value, label }) =>
      value.length <= MAX_WIRE_ENUM_OPTION_CHARS &&
      label.length <= MAX_WIRE_ENUM_OPTION_CHARS
  );

  return fits ? { enumOptions, enumValueLabels } : {};
};

/**
 * Drop every option list when the context as a whole would breach the aggregate
 * ceiling — the same trade as `toWireOptions`, applied once the per-field caps
 * have already passed. The option lists are the only part of the payload that is
 * both large and expendable; the field list itself is the prefill allowlist and
 * cannot be trimmed without silently narrowing what the agent may propose.
 */
const withinAggregateCap = (context: PageContext): PageContext => {
  if (JSON.stringify(context).length <= MAX_WIRE_CONTEXT_CHARS) {
    return context;
  }
  if (!context.activeForm) {
    return context;
  }

  return {
    ...context,
    activeForm: {
      ...context.activeForm,
      fields: context.activeForm.fields.map(
        ({ enumOptions: _values, enumValueLabels: _labels, ...field }) => field
      ),
    },
  };
};

/**
 * Build the page context for the NEXT agent turn.
 *
 * Returns a getter rather than a value: the active form is read as a one-shot
 * SNAPSHOT via `useActiveFormStore.getState()` at send time, deliberately NOT as
 * a reactive subscription. Subscribing would re-render the chat panel on every
 * keystroke in the form (RHF publishes on change) for context that only matters
 * at the moment a message is sent.
 *
 * The getter is also STABLE across renders (`useEffectEvent`), which matters for
 * more than tidiness. A getter that changed identity on navigation would churn
 * the deps of every consumer's `useCallback` — and the starter chips in
 * `useChatOptions` hold onto the sender they were built with, so a chip rendered
 * before a navigation would go on reporting the route the user had left. Reading
 * the latest render's `pathname` through a stable function makes the snapshot
 * honest for the route as well as the form.
 *
 * `activeForm` is present only while a form publishes to the bridge store, which
 * is opt-in per call site (`publishActiveForm`, gated on the feature flag — see
 * AID-382). With no publisher wired, every turn sends route + module only, which
 * is exactly the pre-feature behaviour plus a route hint.
 */
export const usePageContext = (): (() => PageContext) => {
  const { pathname } = useLocation();
  const formPrefillEnabled = useIsFormPrefillEnabled();

  return useEffectEvent((): PageContext => {
    // Gated here as well as at the publisher (AID-382). Belt and braces on the
    // EGRESS direction specifically: with the flag off nothing should publish,
    // so the store is empty anyway — but a future call site that passes
    // `publishActiveForm` without gating it would otherwise start sending form
    // descriptors to the model with the feature switched off, and nothing about
    // that failure is visible from the UI.
    const formState = formPrefillEnabled
      ? useActiveFormStore.getState().formState
      : null;

    return withinAggregateCap({
      route: pathname,
      module: inferModuleFromPathname(pathname),
      ...(formState
        ? {
            activeForm: {
              entityType: formState.entityType,
              recordId: formState.recordId,
              fields: formState.descriptor.map((field) => ({
                key: field.key,
                label: field.label,
                displayType: field.displayType,
                required: field.required,
                ...(field.options ? toWireOptions(field.options) : {}),
              })),
            },
          }
        : {}),
    });
  });
};
